import {
  COURSE_ASSESSMENT_METHOD_DESCRIPTIONS,
  formatAssessmentMethodLabel,
  isAssessmentAccordionTitle,
  resolveCourseAssessment,
  type CourseAssessmentConfig,
} from "@/lib/course-assessments";
import { stripMarkdownSyntax } from "@/utils/blog-themes";
import {
  formatEntryRequirementsContent,
  shouldFormatEntryRequirementsList,
} from "@/utils/entry-requirements-format";

export type CourseFaqItem = {
  question: string;
  answer: string;
};

type ContentIncludes = {
  Entry?: Array<{
    sys?: { id?: string };
    fields?: Record<string, unknown>;
  }>;
};

type CourseEntry = {
  fields?: {
    type?: string;
    url?: string;
    content?: unknown[];
  };
};

function decodeHtmlEntities(value: string): string {
  return value
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&apos;/g, "'")
    .replace(/&#x27;/gi, "'")
    .replace(/&#(\d+);/g, (_, code: string) =>
      String.fromCharCode(Number(code)),
    )
    .replace(/&#x([0-9a-f]+);/gi, (_, hex: string) =>
      String.fromCharCode(parseInt(hex, 16)),
    );
}

function stripHtmlTags(value: string): string {
  return value.replace(/<[^>]*>/g, " ");
}

/**
 * Before collapsing whitespace, flatten markdown/HTML list items the same way
 * a browser textContent walk does: join with spaces, do not invent periods.
 * Invented ";." / "Therapies. VTCT" endings drifted from visible FAQ HTML and
 * failed check:faq.
 */
function sentenceSeparateListItems(value: string): string {
  let text = value.replace(/<li\b[^>]*>([\s\S]*?)<\/li>/gi, (_, inner: string) => {
    const itemText = decodeHtmlEntities(stripHtmlTags(inner))
      .replace(/\s+/g, " ")
      .trim();
    return itemText ? ` ${itemText} ` : " ";
  });

  text = text.replace(
    /^(\s*)([-*+]|\d+\.)\s+(.+?)\s*$/gm,
    (_match, indent: string, _marker: string, item: string) =>
      `${indent}${item.trim()}`,
  );

  return text;
}

/** Plain text matching visible FAQ copy: strip markdown/HTML and decode entities. */
export function toFaqPlainText(value: unknown): string {
  if (value == null) {
    return "";
  }

  let text = "";

  if (typeof value === "string") {
    text = value;
  } else if (typeof value === "object") {
    const parts: string[] = [];
    const walk = (node: unknown) => {
      if (!node || typeof node !== "object") {
        return;
      }
      const record = node as {
        nodeType?: string;
        value?: string;
        content?: unknown[];
      };
      if (record.nodeType === "text" && typeof record.value === "string") {
        parts.push(record.value);
      }
      if (Array.isArray(record.content)) {
        record.content.forEach(walk);
      }
    };
    walk(value);
    text = parts.join(" ");
  } else {
    text = String(value);
  }

  return decodeHtmlEntities(
    stripHtmlTags(stripMarkdownSyntax(sentenceSeparateListItems(text))),
  )
    .replace(/\s+/g, " ")
    .trim();
}

export function isFaqAccordionTitle(title?: string | null): boolean {
  if (!title) {
    return false;
  }
  const normalised = title.trim().toLowerCase();
  return (
    normalised.includes("frequently asked") ||
    normalised === "faq" ||
    normalised === "faqs" ||
    /\bfaqs?\b/.test(normalised)
  );
}

export function buildCourseAssessmentAnswerText(
  assessment: CourseAssessmentConfig,
): string {
  const resolved = resolveCourseAssessment(assessment);
  if (!resolved.methods.length) {
    return "";
  }

  const parts = [
    "Assessment for this VTCT qualification includes the following:",
  ];

  for (const method of resolved.methods) {
    const count = resolved.counts?.[method];
    const labelOverride = resolved.labels?.[method];
    const description =
      resolved.descriptions?.[method] ||
      COURSE_ASSESSMENT_METHOD_DESCRIPTIONS[method];
    parts.push(formatAssessmentMethodLabel(method, count, labelOverride));
    parts.push(description);
  }

  if (resolved.notes) {
    parts.push(resolved.notes);
  }

  return parts.join(" ").replace(/\s+/g, " ").trim();
}

function resolveLinkedEntry(
  item: any,
  includes?: ContentIncludes | null,
): any {
  if (item?.fields) {
    return item;
  }

  const id = item?.sys?.id;
  if (!id || !includes?.Entry?.length) {
    return item;
  }

  return includes.Entry.find((entry) => entry?.sys?.id === id) || item;
}

function faqAnswerFromItem(item: any): string {
  if (item?.courseAssessment) {
    return buildCourseAssessmentAnswerText(item.courseAssessment);
  }
  const content = item?.fields?.content;
  const formatted = shouldFormatEntryRequirementsList(
    item?.fields?.title,
    content,
  )
    ? formatEntryRequirementsContent(content)
    : content;
  return toFaqPlainText(formatted);
}

/**
 * FAQs from the same accordion data the course template renders.
 * Assessment stays in the main course accordion only (not duplicated in FAQs),
 * so FAQPage matches the visible FAQ list word for word.
 */
export function extractCourseFaqs(
  entry?: CourseEntry | null,
  includes?: ContentIncludes | null,
): CourseFaqItem[] {
  if (!entry?.fields || entry.fields.type !== "Course") {
    return [];
  }

  const content = Array.isArray(entry.fields.content)
    ? entry.fields.content
    : [];
  const faqs: CourseFaqItem[] = [];
  const seenQuestions = new Set<string>();

  // Pages with a separate non-FAQ accordion get the canonical assessment
  // answer injected there, so the FAQ accordion's own version (if any) is
  // dropped to avoid duplicating/conflicting copy in the FAQPage schema.
  // Pages with no main accordion (assessment FAQ only lives in the FAQ
  // accordion) keep their authored FAQ item instead of losing it entirely.
  const hasNonFaqAccordion = content.some((block) => {
    const resolvedBlock = resolveLinkedEntry(block, includes);
    return (
      resolvedBlock?.sys?.contentType?.sys?.id === "accordion" &&
      !isFaqAccordionTitle(resolvedBlock?.fields?.title)
    );
  });

  for (const block of content) {
    const resolvedBlock = resolveLinkedEntry(block, includes);
    const contentTypeId = resolvedBlock?.sys?.contentType?.sys?.id;
    if (contentTypeId !== "accordion") {
      continue;
    }

    if (!isFaqAccordionTitle(resolvedBlock?.fields?.title)) {
      continue;
    }

    const items = Array.isArray(resolvedBlock?.fields?.items)
      ? resolvedBlock.fields.items
          .map((item: any) => resolveLinkedEntry(item, includes))
          .filter(
            (item: any) =>
              !hasNonFaqAccordion ||
              !isAssessmentAccordionTitle(item?.fields?.title),
          )
      : [];

    for (const item of items) {
      const question = toFaqPlainText(item?.fields?.title);
      const answer = faqAnswerFromItem(item);
      if (!question || !answer) {
        continue;
      }

      const dedupeKey = question.toLowerCase();
      if (seenQuestions.has(dedupeKey)) {
        continue;
      }
      seenQuestions.add(dedupeKey);
      faqs.push({ question, answer });
    }
  }

  return faqs;
}

export function buildCourseFaqPageJsonLd(
  entry?: CourseEntry | null,
  includes?: ContentIncludes | null,
): Record<string, unknown> | null {
  const faqs = extractCourseFaqs(entry, includes);
  if (!faqs.length) {
    return null;
  }

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };
}
